import React from "react";
import PropTypes from "prop-types";
import KpiCard from "../../../components/ui-components/KpiCard";

// GovernanceKpis — the Enforcement posture strip. Unlike Control's spend tiles, these measure
// GOVERNANCE: how much budgeted spend is gated by a policy, what's ungoverned, and how many
// sign-offs need a decision. Built on the shared KpiCard (mirrors ownership/OwnershipKpis).
const fmtEur = (n) =>
  new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

// A small inline link rendered inside a tile's sub slot (KpiCard has no dedicated CTA slot).
function TileAction({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-1.5 block font-semibold text-primary hover:text-primary-hover"
    >
      {label}
    </button>
  );
}
TileAction.propTypes = { label: PropTypes.string.isRequired, onClick: PropTypes.func };

// Thin coverage bar shown as the coverage tile's chart node.
function CoverageBar({ pct }) {
  const w = Math.max(0, Math.min(100, Math.round((pct || 0) * 100)));
  return (
    <span className="block h-2 w-full overflow-hidden rounded-full bg-surface-2">
      <span className="block h-2 rounded-full bg-primary" style={{ width: `${w}%` }} />
    </span>
  );
}
CoverageBar.propTypes = { pct: PropTypes.number };

export default function GovernanceKpis({ governance, signoffCount = 0, onAttachPolicy, onReviewQueue }) {
  const g = governance || {};
  const coveragePctLabel = Math.round((g.coveragePct || 0) * 100);
  const ungoverned = g.ungovernedCount || 0;

  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: "1.25fr 1fr 1fr" }}>
      <KpiCard
        label="Governance coverage"
        value={
          <span>
            {g.governedCount || 0} of {g.totalCount || 0}{" "}
            <span className="text-sm font-medium text-text-2">budgets governed</span>
          </span>
        }
        tone="neutral"
        chart={<CoverageBar pct={g.coveragePct} />}
        sub={`${coveragePctLabel}% of budgeted spend is gated by a policy`}
      />

      <KpiCard
        label="Ungoverned spend"
        value={fmtEur(g.ungovernedSpendEur)}
        tone={ungoverned > 0 ? "attention" : "ok"}
        sub={
          <>
            {ungoverned > 0
              ? `${ungoverned} budget${ungoverned === 1 ? "" : "s"} · no policy attached`
              : "Every budget is governed by a policy"}
            {ungoverned > 0 ? <TileAction label="Attach policy →" onClick={onAttachPolicy} /> : null}
          </>
        }
      />

      <KpiCard
        label="Approvals pending"
        value={signoffCount}
        tone={signoffCount > 0 ? "attention" : "ok"}
        sub={
          <>
            {signoffCount > 0 ? "awaiting your decision" : "nothing pending"}
            {signoffCount > 0 ? <TileAction label="Review queue →" onClick={onReviewQueue} /> : null}
          </>
        }
      />
    </div>
  );
}

GovernanceKpis.propTypes = {
  governance: PropTypes.shape({
    totalCount: PropTypes.number,
    governedCount: PropTypes.number,
    ungovernedCount: PropTypes.number,
    ungovernedSpendEur: PropTypes.number,
    coveragePct: PropTypes.number,
  }),
  signoffCount: PropTypes.number,
  onAttachPolicy: PropTypes.func,
  onReviewQueue: PropTypes.func,
};
